import { Location } from '@angular/common';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { PatientDepartmentPickerComponent } from '@modules/dashboard/patient-departments-picker/patient-department-picker.component';
import { Patient } from '@pixacare/domain';
import { DialogService } from '@services/dialog.service';
import { PatientHttpService } from '@services/http/patient.http.service';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

const getPatient = (
  patientService: PatientHttpService,
  clientCode: string,
  businessIdentifier: string,
): Observable<Patient> =>
  patientService
    .getPatientsByBusinessIdentifier(clientCode, businessIdentifier)
    .pipe(
      map((patients: Patient[]) =>
        patients.find((patient: Patient) => patient.businessIdentifier === businessIdentifier),
      ),
    );

const getRoutes = (
  clientCode: string,
  patientId: number,
  departmentId: number,
): { routes: string[]; state: string } => {
  if (!departmentId || !patientId) {
    return {
      routes: ['/client', clientCode, 'patients'],
      state: `/client/${clientCode}/patients`,
    };
  }

  if (departmentId === -1) {
    return {
      routes: ['/client', clientCode, 'patients', `${patientId}`],
      state: `/client/${clientCode}/patients`,
    };
  }

  return {
    routes: ['/client', clientCode, 'department', `${departmentId}`, 'patients', `${patientId}`],
    state: `/client/${clientCode}/department/${departmentId}/patients`,
  };
};

const navigate = (router: Router, location: Location, { routes, state }: { routes: string[]; state: string }): void => {
  location.replaceState(state);
  router.navigate(routes, {
    queryParams: { ipp: null },
    queryParamsHandling: 'merge',
  });
};

// TODO : Replace boolean by UrlTree would be a nice improvement
export const redirectToPatientGuard: CanActivateFn = (route: ActivatedRouteSnapshot): Observable<boolean> => {
  const patientService = inject(PatientHttpService);
  const dialogService = inject(DialogService);
  const router = inject(Router);
  const location = inject(Location);
  const translocoService = inject(TranslocoService);

  const clientCode = route.params.clientCode;
  const businessIdentifier = route.queryParams.ipp;
  if (!clientCode || !businessIdentifier) {
    return of(true);
  }

  const openDialog = (patient: Patient): Observable<number> =>
    dialogService.openComponent<PatientDepartmentPickerComponent, number>(PatientDepartmentPickerComponent, {
      size: 'm',
      label: translocoService.translate('Accès au dossier patient'),
      data: {
        patient,
        clientCode,
      },
      closable: false,
      dismissible: false,
    });

  return getPatient(patientService, clientCode, businessIdentifier).pipe(
    switchMap((patient: Patient) =>
      openDialog(patient).pipe(
        map((departmentId: number | null) => {
          navigate(router, location, getRoutes(clientCode, patient?.id, departmentId));
          return true;
        }),
      ),
    ),
    catchError((error) => {
      console.error(error);
      router.navigate(['/']);
      return of(false);
    }),
  );
};
